import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { apiFetch } from "@/lib/api";

import type { Board, ImpactCategory, SwotBoard } from "./constants";

// ─── Types ───────────────────────────────────────────────────────────

export interface Participant {
  id: string;
  session_id: string;
  user_id: string;
  display_name: string;
  email: string;
  color: string;
}

export interface PlanningSession {
  id: string;
  name: string;
  description: string | null;
  status: "active" | "archived";
  ai_model: string | null;
  can_revert_rescale: boolean;
  created_by: string | null;
  created_by_name: string | null;
  note_count: number;
  participants: Participant[];
  created_at: string;
  updated_at: string;
}

export interface Category {
  id: string;
  session_id: string;
  board: Board;
  name: string;
  sort_order: number;
  summary: string | null;
  summary_model: string | null;
  created_by: string | null;
  updated_at: string;
}

export interface Vote {
  user_id: string;
  user_name: string;
  effort: number;
  impact: number;
}

export interface VoteSummary {
  count: number;
  revealed: boolean;
  my_vote: { effort: number; impact: number } | null;
  votes: Vote[];
  avg_effort: number | null;
  avg_impact: number | null;
}

export interface AIReview {
  status?: "ok" | "flagged" | "failed" | "skipped";
  agrees?: boolean;
  suggested_board?: SwotBoard;
  suggested_category?: string | null;
  question?: string | null;
  clarifying_questions?: string[];
  reasoning?: string | null;
  model?: string | null;
  error?: string;
}

export interface Note {
  id: string;
  session_id: string;
  board: Board;
  category_id: string | null;
  title: string;
  description: string | null;
  author_id: string | null;
  author_name: string | null;
  author_color: string | null;
  status: "draft" | "posted";
  sort_order: number;
  impact_category: ImpactCategory | null;
  departments: string[];
  goal_ids: string[];
  pos_x: number | null;
  pos_y: number | null;
  placement_override: boolean;
  votes_revealed: boolean;
  selected: boolean;
  source_category_id: string | null;
  promoted_objective_id: string | null;
  ai_review: AIReview | null;
  ai_clarification: string | null;
  vote_summary: VoteSummary;
  created_at: string;
  updated_at: string;
}

export interface Department {
  id: string;
  name: string;
  sort_order: number;
  is_active: boolean;
}

export interface Workspace {
  session: PlanningSession;
  categories: Category[];
  notes: Note[];
  departments: Department[];
}

export interface GoalOption {
  id: string;
  year: number;
  title: string;
  accent: string;
}

export interface QuarterOption {
  id: string;
  label: string;
  is_closed: boolean;
}

export interface UserOption {
  id: string;
  email: string;
  display_name: string;
}

export interface Options {
  departments: Department[];
  goals: GoalOption[];
  quarters: QuarterOption[];
  users: UserOption[];
}

export interface AIModelInfo {
  name: string;
  size_gb: number | null;
  is_default: boolean;
}

export interface AIModels {
  models: AIModelInfo[];
  default_model: string | null;
  reachable: boolean;
}

export interface AIReviewResult {
  agrees: boolean;
  suggested_board: SwotBoard;
  suggested_category: string | null;
  question: string | null;
  clarifying_questions: string[];
  reasoning: string | null;
  model: string | null;
}

// ─── Query keys ───────────────────────────────────────────────────────

const KEY = ["planning"] as const;
const sessionsKey = [...KEY, "sessions"] as const;
const workspaceKey = (id: string) => [...KEY, "workspace", id] as const;
const optionsKey = [...KEY, "options"] as const;

/** The whole-room poll cadence. Fast enough to feel live in the workshop,
 *  slow enough that ten laptops don't matter. */
const POLL_MS = 2000;

// ─── Queries ──────────────────────────────────────────────────────────

export function useSessions(includeArchived = false) {
  return useQuery({
    queryKey: [...sessionsKey, includeArchived],
    queryFn: () =>
      apiFetch<PlanningSession[]>(
        `/planning/sessions${includeArchived ? "?include_archived=true" : ""}`,
      ),
    staleTime: 30_000,
  });
}

export function useWorkspace(sessionId: string) {
  return useQuery({
    queryKey: workspaceKey(sessionId),
    queryFn: () => apiFetch<Workspace>(`/planning/sessions/${sessionId}`),
    refetchInterval: POLL_MS,
    refetchIntervalInBackground: false,
  });
}

export function useOptions() {
  return useQuery({
    queryKey: optionsKey,
    queryFn: () => apiFetch<Options>("/planning/options"),
    staleTime: 5 * 60_000,
  });
}

export function useAIModels() {
  return useQuery({
    queryKey: [...KEY, "ai-models"],
    queryFn: () => apiFetch<AIModels>("/planning/ai-models"),
    staleTime: 5 * 60_000,
  });
}

// ─── Mutations ────────────────────────────────────────────────────────

/** Every mutation invalidates the workspace; onSuccess handlers also seed the
 *  returned note into the cache so the actor sees their change instantly
 *  instead of on the next poll tick. */
function useWorkspaceInvalidate(sessionId: string) {
  const qc = useQueryClient();
  return {
    seedNote: (note: Note) => {
      qc.setQueryData<Workspace>(workspaceKey(sessionId), (ws) => {
        if (!ws) return ws;
        const exists = ws.notes.some((n) => n.id === note.id);
        return {
          ...ws,
          notes: exists ? ws.notes.map((n) => (n.id === note.id ? note : n)) : [...ws.notes, note],
        };
      });
      void qc.invalidateQueries({ queryKey: workspaceKey(sessionId) });
    },
    invalidate: () => {
      void qc.invalidateQueries({ queryKey: workspaceKey(sessionId) });
    },
  };
}

export function useCreateSession() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { name: string; description?: string | null }) =>
      apiFetch<PlanningSession>("/planning/sessions", { method: "POST", body }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: sessionsKey }),
  });
}

export function useUpdateSession() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      ...body
    }: {
      id: string;
      name?: string;
      description?: string | null;
      status?: "active" | "archived";
      /** "" resets the session to the planning default model. */
      ai_model?: string;
    }) => apiFetch<PlanningSession>(`/planning/sessions/${id}`, { method: "PATCH", body }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: KEY }),
  });
}

export interface NoteCreateBody {
  board: Board;
  title: string;
  description?: string | null;
  impact_category?: ImpactCategory | null;
  departments?: string[] | null;
  goal_ids?: string[] | null;
}

export function useCreateNote(sessionId: string) {
  const { seedNote } = useWorkspaceInvalidate(sessionId);
  return useMutation({
    mutationFn: (body: NoteCreateBody) =>
      apiFetch<Note>(`/planning/sessions/${sessionId}/notes`, { method: "POST", body }),
    onSuccess: seedNote,
  });
}

export interface NoteUpdateBody {
  title?: string;
  description?: string | null;
  impact_category?: ImpactCategory | null;
  departments?: string[] | null;
  goal_ids?: string[] | null;
  ai_clarification?: string | null;
}

export function useUpdateNote(sessionId: string) {
  const { seedNote } = useWorkspaceInvalidate(sessionId);
  return useMutation({
    mutationFn: ({ id, ...body }: { id: string } & NoteUpdateBody) =>
      apiFetch<Note>(`/planning/notes/${id}`, { method: "PATCH", body }),
    onSuccess: seedNote,
  });
}

export function useDeleteNote(sessionId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiFetch<void>(`/planning/notes/${id}`, { method: "DELETE" }),
    onSuccess: (_, id) => {
      qc.setQueryData<Workspace>(workspaceKey(sessionId), (ws) =>
        ws ? { ...ws, notes: ws.notes.filter((n) => n.id !== id) } : ws,
      );
      void qc.invalidateQueries({ queryKey: workspaceKey(sessionId) });
    },
  });
}

export function useAIReview() {
  return useMutation({
    mutationFn: (noteId: string) =>
      apiFetch<AIReviewResult>(`/planning/notes/${noteId}/ai-review`, { method: "POST" }),
  });
}

export interface NotePostBody {
  board: Board;
  category_id?: string | null;
  new_category?: string | null;
  ai_clarification?: string | null;
}

export function usePostNote(sessionId: string) {
  const { seedNote } = useWorkspaceInvalidate(sessionId);
  return useMutation({
    mutationFn: ({ id, ...body }: { id: string } & NotePostBody) =>
      apiFetch<Note>(`/planning/notes/${id}/post`, { method: "POST", body }),
    onSuccess: seedNote,
  });
}

export interface NoteMoveBody {
  board?: Board;
  category_id?: string | null;
  new_category?: string | null;
  clear_category?: boolean;
  sort_order?: number;
}

export function useMoveNote(sessionId: string) {
  const { seedNote } = useWorkspaceInvalidate(sessionId);
  return useMutation({
    mutationFn: ({ id, ...body }: { id: string } & NoteMoveBody) =>
      apiFetch<Note>(`/planning/notes/${id}/move`, { method: "POST", body }),
    onSuccess: seedNote,
  });
}

export function usePositionNote(sessionId: string) {
  const { seedNote } = useWorkspaceInvalidate(sessionId);
  return useMutation({
    mutationFn: ({ id, x, y }: { id: string; x: number; y: number }) =>
      apiFetch<Note>(`/planning/notes/${id}/position`, { method: "POST", body: { x, y } }),
    onSuccess: seedNote,
  });
}

export function useVoteNote(sessionId: string) {
  const { seedNote } = useWorkspaceInvalidate(sessionId);
  return useMutation({
    mutationFn: ({ id, effort, impact }: { id: string; effort: number; impact: number }) =>
      apiFetch<Note>(`/planning/notes/${id}/vote`, { method: "POST", body: { effort, impact } }),
    onSuccess: seedNote,
  });
}

export function useRevealNote(sessionId: string) {
  const { seedNote } = useWorkspaceInvalidate(sessionId);
  return useMutation({
    mutationFn: (id: string) => apiFetch<Note>(`/planning/notes/${id}/reveal`, { method: "POST" }),
    onSuccess: seedNote,
  });
}

export function useRevealAll(sessionId: string) {
  const { invalidate } = useWorkspaceInvalidate(sessionId);
  return useMutation({
    mutationFn: () =>
      apiFetch<void>(`/planning/sessions/${sessionId}/reveal-all`, { method: "POST" }),
    onSuccess: invalidate,
  });
}

export function useRescaleGrid(sessionId: string) {
  const { invalidate } = useWorkspaceInvalidate(sessionId);
  return useMutation({
    mutationFn: () =>
      apiFetch<void>(`/planning/sessions/${sessionId}/rescale`, { method: "POST" }),
    onSuccess: invalidate,
  });
}

export function useRevertRescale(sessionId: string) {
  const { invalidate } = useWorkspaceInvalidate(sessionId);
  return useMutation({
    mutationFn: () =>
      apiFetch<void>(`/planning/sessions/${sessionId}/rescale-revert`, { method: "POST" }),
    onSuccess: invalidate,
  });
}

export function useSelectNote(sessionId: string) {
  const { seedNote } = useWorkspaceInvalidate(sessionId);
  return useMutation({
    mutationFn: ({ id, selected }: { id: string; selected: boolean }) =>
      apiFetch<Note>(`/planning/notes/${id}/select`, { method: "POST", body: { selected } }),
    onSuccess: seedNote,
  });
}

export function usePromoteObjective(sessionId: string) {
  const { invalidate } = useWorkspaceInvalidate(sessionId);
  return useMutation({
    mutationFn: ({
      id,
      ...body
    }: {
      id: string;
      quarter_id: string;
      owner_id?: string | null;
      goal_id?: string | null;
      priority?: number;
    }) =>
      apiFetch<{ objective_id: string }>(`/planning/notes/${id}/promote-objective`, {
        method: "POST",
        body,
      }),
    onSuccess: invalidate,
  });
}

export function useInviteParticipant(sessionId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { user_id: string; color?: string | null }) =>
      apiFetch<Participant>(`/planning/sessions/${sessionId}/participants`, {
        method: "POST",
        body,
      }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useUpdateParticipant() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, color }: { id: string; color: string }) =>
      apiFetch<Participant>(`/planning/participants/${id}`, { method: "PATCH", body: { color } }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useRemoveParticipant() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<void>(`/planning/participants/${id}`, { method: "DELETE" }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useCreateCategory(sessionId: string) {
  const { invalidate } = useWorkspaceInvalidate(sessionId);
  return useMutation({
    mutationFn: (body: { board: Board; name: string }) =>
      apiFetch<Category>(`/planning/sessions/${sessionId}/categories`, { method: "POST", body }),
    onSuccess: invalidate,
  });
}

export function useUpdateCategory(sessionId: string) {
  const { invalidate } = useWorkspaceInvalidate(sessionId);
  return useMutation({
    mutationFn: ({ id, ...body }: { id: string; name?: string; sort_order?: number }) =>
      apiFetch<Category>(`/planning/categories/${id}`, { method: "PATCH", body }),
    onSuccess: invalidate,
  });
}

export function useDeleteCategory(sessionId: string) {
  const { invalidate } = useWorkspaceInvalidate(sessionId);
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<void>(`/planning/categories/${id}`, { method: "DELETE" }),
    onSuccess: invalidate,
  });
}

export function useSummarizeCategory(sessionId: string) {
  const { invalidate } = useWorkspaceInvalidate(sessionId);
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<{ summary: string; model: string | null }>(
        `/planning/categories/${id}/summarize`,
        { method: "POST" },
      ),
    onSuccess: invalidate,
  });
}

export function usePromoteCategory(sessionId: string) {
  const { seedNote } = useWorkspaceInvalidate(sessionId);
  return useMutation({
    mutationFn: ({ id, impact_category }: { id: string; impact_category?: ImpactCategory | null }) =>
      apiFetch<Note>(`/planning/categories/${id}/promote`, {
        method: "POST",
        body: { impact_category: impact_category ?? null },
      }),
    onSuccess: seedNote,
  });
}

export function useAutoCategorize(sessionId: string) {
  const { invalidate } = useWorkspaceInvalidate(sessionId);
  return useMutation({
    mutationFn: (board: SwotBoard) =>
      apiFetch<{ assigned: number; created_categories: string[] }>(
        `/planning/sessions/${sessionId}/boards/${board}/auto-categorize`,
        { method: "POST" },
      ),
    onSuccess: invalidate,
  });
}
